import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors"
          >
            <span className="font-heading text-xl font-bold whitespace-nowrap">
              VigorBuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-foreground-700 text-sm font-medium hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              <span>Home</span>
            </Link>

            <Link
              to="/shopping-guide"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-foreground-700 text-sm font-medium hover:bg-background-100 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line"></i>
              <span>Shopping Guide</span>
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/60 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-4-line text-primary-500"></i>
              </div>
              <span className="font-medium">Home</span>
            </Link>

            <Link
              to="/shopping-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-500 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-shopping-bag-3-line text-accent-500"></i>
              </div>
              <span className="font-medium">Shopping Guide</span>
            </Link>

            <button
              onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200">
                  <i className="ri-apps-2-line text-foreground-600"></i>
                </div>
                <span className="font-medium">Browse Categories</span>
              </div>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCategoryOpen && (
              <div className="pl-3 space-y-0.5 pt-1 pb-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="pt-2 pb-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-center px-4 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}